import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomiciliacionCtService } from 'src/app/services/domiciliacion-ct/domiciliacion-ct.service';

/** «Aceptación CrediTotal»: avance de las cuentas domiciliadas que ya aceptaron los términos de CrediTotal. */
@Component({
  selector: 'app-domiciliacion-ct-avance',
  templateUrl: './domiciliacion-ct-avance.component.html'
})
export class DomiciliacionCtAvanceComponent implements OnInit, OnDestroy {
  datos: any = null;
  cargando = false;
  error: string | null = null;
  sinPermiso = false;
  /** El servidor calcula en segundo plano: mientras tanto se vuelve a preguntar cada 15 s. */
  calculando = false;
  private reintento: any = null;
  descargando = false;
  errorExcel: string | null = null;

  constructor(private servicio: DomiciliacionCtService) {}

  ngOnInit(): void {
    this.cargar(false);
  }

  ngOnDestroy(): void {
    clearTimeout(this.reintento);
  }

  async cargar(refrescar: boolean): Promise<void> {
    this.cargando = true;
    this.error = null;
    try {
      const r = await this.servicio.getAvance(refrescar);
      this.calculando = !!r?.calculando;
      if (r?.success === false) this.error = r?.message || 'No se pudo consultar el avance';
      else if (r?.total) this.datos = r;
      else if (!this.calculando && r?.error_calculo) this.error = 'No se pudo calcular el avance. Intente de nuevo en un momento.';
      this.sinPermiso = false;
      clearTimeout(this.reintento);
      if (this.calculando) this.reintento = setTimeout(() => this.cargar(false), 15000);
    } catch (e: any) {
      if (e?.status === 403) this.sinPermiso = true;
      else this.error = e?.error?.message || e?.error?.msg || 'No se pudo consultar el avance. Intente de nuevo en un momento.';
    } finally {
      this.cargando = false;
    }
  }

  /** Baja el Excel con quién aceptó y quién falta (para las notificaciones push). */
  async descargarExcel(): Promise<void> {
    this.descargando = true;
    this.errorExcel = null;
    try {
      const blob = await this.servicio.descargarAvanceExcel();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aceptacion_ct_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      this.errorExcel = e?.status === 403
        ? 'No tiene permiso para descargar el Excel.'
        : 'No se pudo descargar el Excel. Intente de nuevo en un momento.';
    } finally {
      this.descargando = false;
    }
  }

  num(n: any): string {
    return Number(n || 0).toLocaleString('es-VE');
  }

  /** Con aceptaciones que redondean a 0 (p. ej. 1 de 150.000) muestra «<0,1%» en vez de 0%. */
  pct(n: any, aceptaron?: any): string {
    if (!Number(n) && Number(aceptaron) > 0) return '<0,1%';
    return Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + '%';
  }

  ancho(n: any): string {
    return Math.max(0, Math.min(100, Number(n) || 0)) + '%';
  }

  producto(p: string): string {
    return p === 'SEGUROS_MERCANTIL' ? 'Seguros' : p === 'CREDIT' ? 'Crédito' : p || '—';
  }

  get vacio(): boolean {
    return !!this.datos && !(this.datos.total?.universo > 0);
  }

  get minutos(): number {
    const t = this.datos?.generado_at ? new Date(this.datos.generado_at).getTime() : NaN;
    return isNaN(t) ? 0 : Math.max(0, Math.floor((Date.now() - t) / 60000));
  }

  get textoActualizado(): string {
    const m = this.minutos;
    return m < 1 ? 'Actualizado hace menos de 1 min' : `Actualizado hace ${m} min`;
  }

  /** Últimos 30 días, uno por día aunque no haya aceptaciones, con el acumulado y su % del universo. */
  get dias(): { fecha: string; aceptaron: number; acumulado: number; pct: number }[] {
    if (!this.datos) return [];
    const porFecha = new Map<string, number>((this.datos.por_dia || []).map((d: any) => [d.fecha, Number(d.aceptaron) || 0]));
    const enVentana = Array.from(porFecha.values()).reduce((a, b) => a + b, 0);
    let acumulado = Math.max(0, (Number(this.datos.total?.aceptaron) || 0) - enVentana);
    const universo = Number(this.datos.total?.universo) || 0;
    const hoy = new Date();
    const lista = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - i);
      const fecha = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const aceptaron = porFecha.get(fecha) || 0;
      acumulado += aceptaron;
      lista.push({ fecha, aceptaron, acumulado, pct: universo ? Math.min(100, (acumulado * 100) / universo) : 0 });
    }
    return lista;
  }

  /** Hoy, ayer y promedio diario de los últimos 7 días. */
  get ritmo(): { hoy: number; ayer: number; promedio7: number } {
    const d = this.dias;
    const ult7 = d.slice(-7).reduce((a, x) => a + x.aceptaron, 0);
    return { hoy: d[d.length - 1]?.aceptaron || 0, ayer: d[d.length - 2]?.aceptaron || 0, promedio7: Math.round(ult7 / 7) };
  }

  tituloDia(d: { fecha: string; aceptaron: number; acumulado: number; pct: number }): string {
    const pct = d.pct > 0 && d.pct < 0.1 ? '<0,1%' : d.pct.toLocaleString('es-VE', { maximumFractionDigits: 1 }) + '%';
    return `${this.fechaCorta(d.fecha)}: ${this.num(d.aceptaron)} aceptaron · acumulado ${this.num(d.acumulado)} (${pct})`;
  }

  get maxDia(): number {
    return Math.max(1, ...this.dias.map((d) => d.aceptaron));
  }

  altoBarra(n: any): string {
    const v = Number(n) || 0;
    return v ? Math.max(4, Math.round((v / this.maxDia) * 100)) + '%' : '2px';
  }

  fechaCorta(f: string): string {
    const [, m, d] = String(f || '').split('-');
    return d && m ? `${d}/${m}` : f;
  }

  textoInvitacion(): string {
    const i = this.datos?.invitacion;
    return i === 'TODOS' ? 'Invitando a todos' : i === 'PILOTO' ? 'Invitando solo al piloto' : 'Invitación apagada';
  }
}
